/**
 * Clubaanvragen in de uitclub-keuze (eigenaar 01-10-2026).
 *
 * Een club maakt geen tegenstanders meer aan. Staat de tegenstander nergens,
 * dan vraagt de club hem aan (auth: POST /ClubRequest) en kiest hij die
 * aanvraag in de uitclub-lijst als `aanvraag:<id>`. Die waarde is geen
 * clubalbum en mag nooit bij arch/arena aankomen: de wedstrijd wordt
 * opgeslagen zonder uitclub, en daarna aan de aanvraag gekoppeld
 * (PUT /ClubRequest/{id}/Match). Keurt een super-admin de aanvraag goed,
 * dan vult de beheerconsole de uitclub op die wedstrijden in.
 *
 * Hier alleen pure functies; de interceptor (club-aanvraag.interceptor.ts)
 * gebruikt ze.
 */

export const AANVRAAG_PREFIX = 'aanvraag:';

export function aanvraagSid(id: number | string): string {
  return `${AANVRAAG_PREFIX}${id}`;
}

export function isAanvraagSid(sid: unknown): sid is string {
  return typeof sid === 'string' && sid.startsWith(AANVRAAG_PREFIX);
}

/** Het aanvraagnummer uit `aanvraag:<id>`, of null. */
export function aanvraagId(sid: unknown): number | null {
  if (!isAanvraagSid(sid)) {
    return null;
  }
  const id = Number(sid.slice(AANVRAAG_PREFIX.length));
  return Number.isInteger(id) && id > 0 ? id : null;
}

/** Waar in de body de wedstrijdkanten stonden. */
export type AanvraagPlek = 'wedstrijd' | 'genest';

export interface AanvraagUitBody {
  /** Kopie van de body zonder `aanvraag:`-waarden. */
  body: any;
  /** De aanvraagnummers die eruit gehaald zijn (uniek). */
  ids: number[];
  /**
   * `wedstrijd`: home/away staan bovenaan (POST/PATCH /Match).
   * `genest`: onder `match` (POST/PATCH /Scheduler, POST /liveapp/sessions).
   */
  plek: AanvraagPlek;
}

const KANTEN = ['home', 'away'] as const;

function isObject(value: unknown): value is Record<string, any> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Kanten (`home`/`away`) van één object schoonmaken. Geeft null als er geen
 * aanvraag in stond, anders een kopie plus de gevonden nummers.
 */
function schoonKanten(houder: Record<string, any>): { kopie: Record<string, any>; ids: number[] } | null {
  let kopie: Record<string, any> | null = null;
  const ids: number[] = [];
  for (const kant of KANTEN) {
    const zijde = houder[kant];
    if (!isObject(zijde) || !isAanvraagSid(zijde['clubAlbum'])) {
      continue;
    }
    const id = aanvraagId(zijde['clubAlbum']);
    if (id !== null && !ids.includes(id)) {
      ids.push(id);
    }
    kopie ??= { ...houder };
    const schoon: Record<string, any> = { ...zijde, clubAlbum: '' };
    if ('name' in zijde) {
      schoon['name'] = '';
    }
    kopie[kant] = schoon;
  }
  return kopie ? { kopie, ids } : null;
}

/**
 * Haalt `aanvraag:`-waarden uit een body die naar arch gaat. Kijkt naar de
 * twee vormen die de formulieren echt sturen: home/away bovenaan (Match) en
 * home/away onder `match` (Scheduler, liveapp-sessie). Geeft null als er
 * niets te doen is; de body zelf wordt nooit aangepast.
 */
export function haalAanvraagUitBody(body: unknown): AanvraagUitBody | null {
  if (!isObject(body) || body instanceof FormData || body instanceof Blob) {
    return null;
  }

  const boven = schoonKanten(body);
  if (boven) {
    return { body: boven.kopie, ids: boven.ids, plek: 'wedstrijd' };
  }

  const match = body['match'];
  if (isObject(match)) {
    const genest = schoonKanten(match);
    if (genest) {
      return { body: { ...body, match: genest.kopie }, ids: genest.ids, plek: 'genest' };
    }
  }
  return null;
}

/**
 * De sid van de opgeslagen wedstrijd uit het antwoord van arch:
 * - Match: `{ sid }`
 * - Scheduler: `{ match: { reference } }`
 * - liveapp-sessie: `{ match: '<sid>' }`
 */
export function wedstrijdSidUitAntwoord(antwoord: unknown, plek: AanvraagPlek): string | null {
  if (!isObject(antwoord)) {
    return null;
  }
  if (plek === 'wedstrijd') {
    return typeof antwoord['sid'] === 'string' && antwoord['sid'] ? antwoord['sid'] : null;
  }
  const match = antwoord['match'];
  if (typeof match === 'string') {
    return match || null;
  }
  if (isObject(match)) {
    const sid = match['reference'] || match['sid'];
    return typeof sid === 'string' && sid ? sid : null;
  }
  return null;
}
